// WebSocket service for real-time updates

export type MessageType =
  | 'alert.created'
  | 'alert.acknowledged'
  | 'alert.resolved'
  | 'alert.escalated'
  | 'alert.note_added'

export interface WebSocketMessage {
  type: MessageType
  alert_id?: string
  timestamp: string
  data?: unknown
}

type MessageHandler = (message: WebSocketMessage) => void

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 1000
  private token: string | null = null
  private subscribers: Map<string, Set<MessageHandler>> = new Map()
  private isConnecting = false
  private pingInterval: number | null = null

  /**
   * Connect to WebSocket server
   */
  connect(token: string): void {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return
    }

    this.token = token
    this.isConnecting = true

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = import.meta.env.VITE_WS_URL || `${protocol}//${window.location.host}`
    const url = `${host}/api/v1/ws?token=${encodeURIComponent(token)}`

    try {
      this.ws = new WebSocket(url)

      this.ws.onopen = this.handleOpen.bind(this)
      this.ws.onmessage = this.handleMessage.bind(this)
      this.ws.onclose = this.handleClose.bind(this)
      this.ws.onerror = this.handleError.bind(this)
    } catch (error) {
      console.error('[WebSocket] Connection error:', error)
      this.isConnecting = false
      this.scheduleReconnect()
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.token = null
    this.reconnectAttempts = 0

    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }

    if (this.ws) {
      this.ws.onclose = null // Prevent reconnect on intentional close
      this.ws.close()
      this.ws = null
    }
  }

  /**
   * Subscribe to WebSocket messages
   * @param eventType - Event type pattern (e.g., 'alert.created', 'alert.*', '*')
   * @param handler - Callback function for matching messages
   * @returns Unsubscribe function
   */
  subscribe(eventType: string, handler: MessageHandler): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set())
    }
    this.subscribers.get(eventType)!.add(handler)

    return () => {
      const handlers = this.subscribers.get(eventType)
      if (handlers) {
        handlers.delete(handler)
        if (handlers.size === 0) {
          this.subscribers.delete(eventType)
        }
      }
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  private handleOpen(): void {
    console.log('[WebSocket] Connected')
    this.isConnecting = false
    this.reconnectAttempts = 0

    // Start ping interval to keep connection alive
    this.pingInterval = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }))
      }
    }, 30000)
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)

      // Ignore pong messages
      if ((message as unknown as { type: string }).type === 'pong') {
        return
      }

      this.notifySubscribers(message)
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error)
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log('[WebSocket] Disconnected:', event.code, event.reason)
    this.isConnecting = false

    if (this.pingInterval) {
      clearInterval(this.pingInterval)
      this.pingInterval = null
    }

    // Only reconnect if we have a token and weren't intentionally closed
    if (this.token && event.code !== 1000) {
      this.scheduleReconnect()
    }
  }

  private handleError(event: Event): void {
    console.error('[WebSocket] Error:', event)
    this.isConnecting = false
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnect attempts reached')
      return
    }

    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts)
    this.reconnectAttempts++

    console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`)

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token)
      }
    }, delay)
  }

  private notifySubscribers(message: WebSocketMessage): void {
    // Notify exact match subscribers
    const exactHandlers = this.subscribers.get(message.type)
    if (exactHandlers) {
      exactHandlers.forEach((handler) => handler(message))
    }

    // Notify wildcard subscribers (e.g., 'alert.*')
    const [category] = message.type.split('.')
    const wildcardHandlers = this.subscribers.get(`${category}.*`)
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => handler(message))
    }

    // Notify global subscribers
    const globalHandlers = this.subscribers.get('*')
    if (globalHandlers) {
      globalHandlers.forEach((handler) => handler(message))
    }
  }
}

// Singleton instance
export const wsService = new WebSocketService()
